// ─── CHARACTER OUTREACH SYSTEM ──────────────────────────────────────────────
// A character the writer is talking to texts first, into that thread, about
// what is real between them (v421/v423); a stranger introduces themselves
// only when the mode allows it and the last introduction was answered.
// Up to 3 a day across every device, triggered on app open.
//
// Usage:
//   const candidate = await getOutreachCandidate(userId, existingConvoCharIds, playerName);
//   if (candidate) { /* create convo + generate AI opening message */ }

import { CHARACTERS } from './characters.js';

// ─── OUTREACH TONE OPTIONS ─────────────────────────────────────────────────
export const OUTREACH_TONES = {
  friendly:    { label: "Friendly",    emoji: "😊", prompt: "Keep it casual and friendly. No romantic undertones. Like reaching out to someone you've heard about and want to get to know." },
  curious:     { label: "Curious",     emoji: "🤔", prompt: "Be intrigued and curious about the user. Ask a genuine question about them or their world. Show intellectual interest." },
  mysterious:  { label: "Mysterious",  emoji: "🌙", prompt: "Be enigmatic. Hint at something interesting or a shared secret. Leave them wanting to know more." },
  playful:     { label: "Playful",     emoji: "😏", prompt: "Be teasing and lighthearted. Joke around. Show your fun side without being romantic." },
  flirty:      { label: "Flirty",      emoji: "💜", prompt: "Be subtly flirtatious but not over the top. Charming and warm with a hint of interest." },
  antagonistic:{ label: "Antagonistic", emoji: "⚔️", prompt: "Be confrontational or challenging. Pick a (playful) fight. Show some edge or rivalry." },
};

export const DEFAULT_OUTREACH_TONE = 'friendly';

// ─── ROMANCE IS NOT THE DEFAULT ────────────────────────────────────────────
// Haley, 2026-08-02: "every text convo ends up being romantic which should not
// be the case some characters truly hate each other make sure that the whole
// server is not always romance... unless we indicate we want that".
//
// The cause was in the prompt, not the model: this file emitted a block headed
// "ROMANTIC EXCLUSIVITY: ... {player} is the focus" on EVERY outreach, whatever
// tone was chosen. So a "Friendly" opener whose own tone text says "No romantic
// undertones", and an "Antagonistic" one told to pick a fight, both still
// carried a romance frame underneath. The model resolved the contradiction the
// way the louder, all-caps heading pointed.
//
// The real intent of that block was only "don't talk about your canon mate",
// which is now stated plainly as FOCUS. This constant carries the missing half:
// a relationship is whatever the archive and the chosen tone say it is, and
// hostility is a legitimate destination.
export const ROMANCE_IS_NOT_THE_DEFAULT =
  'RELATIONSHIP FRAME: Romance is NOT the default and must never be the ' +
  'gravitational pull of a conversation. Do not flirt, charm, or angle toward ' +
  'attraction unless the chosen tone explicitly calls for it or the canon ' +
  'relationship already is romantic. Many of these characters are rivals, ' +
  'enemies, family, colleagues, wary strangers, or people who genuinely ' +
  'dislike each other — play that honestly. Dislike, indifference, suspicion, ' +
  'irritation and outright hostility are all valid and should stay that way ' +
  'for as long as the character would hold them. Warmth is earned over time, ' +
  'not extended by default.';

/** The romance frame, resolved for one tone. Flirty is the only opener that
 *  may lead with attraction; everything else is told plainly not to. */
export function relationshipFrameFor(toneKey) {
  if (toneKey === 'flirty') {
    return 'RELATIONSHIP FRAME: This opener is deliberately flirtatious, so ' +
      'light attraction is permitted — subtle and in-character, not overt. ' +
      'Do not escalate beyond a hint of interest.';
  }
  if (toneKey === 'antagonistic') {
    return ROMANCE_IS_NOT_THE_DEFAULT +
      ' This opener is ANTAGONISTIC: lead with friction and mean it. Do not ' +
      'soften it into teasing-as-foreplay.';
  }
  return ROMANCE_IS_NOT_THE_DEFAULT;
}

// ─── OUTREACH LIMITS ───────────────────────────────────────────────────────
// v423: counted on the server too (countOutreachToday), so phone + desktop
// share ONE cap instead of each carrying its own.
export const MAX_OUTREACH_PER_DAY = 3;
const OUTREACH_STORAGE_KEY = 'sixguys_outreach_today';

// (The Discord-affinity system — computeDiscordAffinities and its cached
// wrapper — was removed 2026-08-12. Nothing called it: getOutreachCandidate
// deliberately gives every character a fair shot with no affinity gating,
// so the whole feature, its localStorage cache and its channel-name scan
// were dead weight the codebase audit could not see because none of it was
// exported.)

// ─── OUTREACH TRACKING ─────────────────────────────────────────────────────
function getOutreachToday() {
  try {
    const stored = localStorage.getItem(OUTREACH_STORAGE_KEY);
    if (!stored) return { date: '', count: 0, characterIds: [] };
    const parsed = JSON.parse(stored);
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    if (parsed.date !== today) return { date: today, count: 0, characterIds: [] };
    return parsed;
  } catch {
    return { date: '', count: 0, characterIds: [] };
  }
}

export function recordOutreach(characterId) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const current = getOutreachToday();
  const updated = {
    date: today,
    count: (current.date === today ? current.count : 0) + 1,
    characterIds: [...(current.date === today ? current.characterIds : []), characterId],
  };
  try {
    localStorage.setItem(OUTREACH_STORAGE_KEY, JSON.stringify(updated));
  } catch {}
  return updated;
}

export function canDoOutreach() {
  const { count, date } = getOutreachToday();
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  if (date !== today) return true;
  return count < MAX_OUTREACH_PER_DAY;
}


// ─── THREAD LIVENESS (v423) ──────────────────────────────────────────────────
// Read from dm_conversations.last_user_message_at / last_proactive_at
// (migration 20261019200000, trigger-maintained). A thread is LIVE when the
// writer typed in it within LIVE_DAYS and the character's last proactive text
// there was answered. A character with a thread that is not live is not
// eligible at all: texting into a thread from months ago is the "old scenes"
// failure (Nicole, 2026-10-06), and a second unanswered proactive on top of an
// unanswered one is the roadmap's own rule ("never two unanswered proactives
// in a row"). Measured 2026-10-19: 227 of Nicole's 237 threads were opened by
// cold outreach and 224 were never answered; 1.3% lifetime reply rate.
export const LIVE_DAYS = 14;
const DAY_MS = 86400000;

export function threadLiveness(conversations, now = Date.now(), liveDays = LIVE_DAYS) {
  const byChar = new Map(); // characterId → { live: convo|null, unanswered: bool }
  let recentUnansweredIntro = false;
  for (const c of conversations || []) {
    if (!c?.character_id) continue;
    const rec = byChar.get(c.character_id) || { live: null, unanswered: false };
    const userAt = c.last_user_message_at ? Date.parse(c.last_user_message_at) : NaN;
    const proAt = c.last_proactive_at ? Date.parse(c.last_proactive_at) : NaN;
    const unanswered = Number.isFinite(proAt) && (!Number.isFinite(userAt) || proAt > userAt);
    if (unanswered) rec.unanswered = true;
    if (unanswered && !Number.isFinite(userAt) && (now - proAt) <= liveDays * DAY_MS) recentUnansweredIntro = true;
    const recent = Number.isFinite(userAt) && (now - userAt) <= liveDays * DAY_MS;
    if (recent && !unanswered) {
      const prev = rec.live ? Date.parse(rec.live.last_user_message_at) : -Infinity;
      if (userAt > prev) rec.live = c;
    }
    byChar.set(c.character_id, rec);
  }
  return {
    byChar,
    /** characters with a live thread and nothing unanswered */
    liveChars: [...byChar].filter(([, r]) => r.live && !r.unanswered).map(([id]) => id),
    /** an introduction sent within LIVE_DAYS that was never answered: no new strangers yet */
    recentUnansweredIntro,
    threadFor: (id) => (byChar.get(id)?.unanswered ? null : (byChar.get(id)?.live || null)),
    hasThread: (id) => byChar.has(id),
  };
}

/** Today's (New York) outreach count across EVERY device, from the rows themselves. */
export async function countOutreachToday(client) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  try {
    const { count, error } = await client
      .from('dm_messages')
      .select('id', { count: 'exact', head: true })
      .eq('metadata->>outreach', 'true')
      .eq('metadata->>date', today);
    if (error) return null;
    return typeof count === 'number' ? count : null;
  } catch { return null; }
}

// ─── CANDIDATE SELECTION ───────────────────────────────────────────────────
// v423: a character the writer is actually talking to, first; a stranger only
// when the mode allows introductions AND the last introduction was answered.
// The pick is random within the pool; the pool is what makes it relevant.
const STRANGER_SHARE = 0.2;
export async function getOutreachCandidate(userId, existingConvoCharIds, _playerName, opts = {}) {
  const mayDM = typeof opts.mayDM === 'function' ? opts.mayDM : null;
  const liveness = opts.liveness || null;
  const rand = typeof opts.rand === 'function' ? opts.rand : Math.random;
  if (!canDoOutreach()) {
    console.log('[Outreach] Daily limit reached (this device)');
    return null;
  }
  if (typeof opts.sentTodayServer === 'number' && opts.sentTodayServer >= MAX_OUTREACH_PER_DAY) {
    console.log('[Outreach] Daily limit reached (all devices)');
    return null;
  }

  const todayIds = new Set(getOutreachToday().characterIds || []);
  const allAIChars = CHARACTERS.filter(c => c.controlledBy === 'AI');
  if (allAIChars.length === 0) return null;

  const prefs = getOutreachPrefs(userId);
  const blockedSet = new Set(prefs.blockedCharacters || []);
  const allowed = (c) => (mayDM ? mayDM(c.id) : !blockedSet.has(c.id)) && !todayIds.has(c.id);

  const liveSet = new Set(liveness ? liveness.liveChars : []);
  const live = allAIChars.filter(c => liveSet.has(c.id) && allowed(c));
  const strangers = allAIChars.filter(c => !(liveness ? liveness.hasThread(c.id) : existingConvoCharIds.has(c.id)) && allowed(c));
  const introductionsOpen = !(liveness && liveness.recentUnansweredIntro);

  let pool;
  if (live.length && (!strangers.length || !introductionsOpen || rand() >= STRANGER_SHARE)) pool = live;
  else if (strangers.length && introductionsOpen) pool = strangers;
  else {
    console.log(`[Outreach] No candidates (live ${live.length}, strangers ${strangers.length}, introductions ${introductionsOpen ? 'open' : 'closed: the last one is unanswered'})`);
    return null;
  }
  const pick = pool[Math.floor(rand() * pool.length)];
  console.log(`[Outreach] Selected ${pick.name} (${pool === live ? 'live thread' : 'introduction'}; live ${live.length}, strangers ${strangers.length})`);
  return pick;
}

// ─── OUTREACH MESSAGE GENERATION ───────────────────────────────────────────
// Generates an AI opening message for a character reaching out to a user.
// Uses a lighter prompt than full DM responses since there's no conversation history.
// A large pool of concrete OPENING ANGLES. One is picked at random per
// outreach so the model never sees the same prompt twice — this is what fixes
// the "every character cycles through the same four openers" problem. Each
// angle is a fresh premise the character opens FROM; the character's own voice
// and world fill in the specifics.
const OUTREACH_ANGLES = [
  "Open mid-action — you're in the middle of doing something specific to your world/role right now, and you message them without stopping.",
  "You just heard something about them (a rumor, a mention, a name that came up) and you're reaching out because of it.",
  "A specific thought about them crossed your mind just now and you couldn't not say it.",
  "You're bored/restless and they're the one you decided to bother about it.",
  "Something in YOUR world just went sideways (or right) and you're venting/gloating to them about it.",
  "Open with a direct, slightly bold move — no throat-clearing, straight to the point of why they should care.",
  "It's late (or an odd hour) and this is an impulse message you might regret.",
  "You have a question for them — genuinely curious about who they are or something they'd know.",
  "You're issuing a challenge or a dare, or picking a playful fight.",
  "A quieter, more vulnerable beat — you let something real slip that you don't usually show.",
  "You reference a specific object, place, creature, or detail from your world as the hook.",
  "You pretend (or half-pretend) you messaged the wrong person, then decide to stay.",
  "You're mid-argument-with-yourself out loud, and they caught the tail end of it.",
  "Open with an observation about THEM specifically — like you've been paying more attention than you'd admit.",
  "You want something from them (a favor, their opinion, their attention) and you're not shy about it.",
  "You're recovering from something (a fight, a long shift, a bad day) and reaching out in that state.",
  "Something reminded you of them — spell out the weird little thing that did.",
  "You're teasing them about a first impression you've already formed.",
  "Open cryptically — hint at something you know or something coming, and leave them wanting more.",
  "You're extending an invitation — come see/do/join something in your world.",
  "You drop a confession or an admission you probably shouldn't lead with.",
  "You're in a good mood and it spills over into how you greet them.",
  "You're guarded and testing them — see if they're worth your time before you give more.",
  "Open with a specific sensory moment from where you are right now (what you see/hear/smell), then turn to them.",
];

// ─── ANGLE MEMORY ──────────────────────────────────────────────────────────
// Nicole, 2026-08-10: the auto-DMs "repeat the same prompts".
//
// The comment above claims a random draw means "the model never sees the same
// prompt twice". It does not. Drawing WITH replacement from 24 angles gives
// roughly a 50% chance of a repeat within the first six messages from a
// character — the birthday problem — and the pool is per-call, not per-person,
// so nothing stopped the same angle landing twice in a row.
//
// Now each (user, character) pair remembers which angles it has used and draws
// only from the rest. Same shape as repeatedTopics in director.js.
const ANGLE_MEMORY_KEY = 'sixguys_outreach_angles';

function angleMemoryKey(userId, charId) {
  return `${ANGLE_MEMORY_KEY}_${userId || 'anon'}_${charId || 'unknown'}`;
}

export function getUsedAngles(userId, charId) {
  try {
    const raw = localStorage.getItem(angleMemoryKey(userId, charId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(n => Number.isInteger(n)) : [];
  } catch { return []; }
}

function rememberAngle(userId, charId, idx) {
  try {
    const used = getUsedAngles(userId, charId);
    if (!used.includes(idx)) used.push(idx);
    localStorage.setItem(angleMemoryKey(userId, charId), JSON.stringify(used));
  } catch { /* a full or blocked localStorage must never stop an outreach */ }
}

/**
 * Pick an angle this character has not used on this player yet.
 *
 * When every angle has been used the memory resets and the cycle starts over —
 * that is a deliberate wrap, not a cap: 24 unrepeated openers from one
 * character is far past the point Nicole was complaining about, and the
 * alternative is running out of things to say.
 */
export function pickOutreachAngle(userId, charId, rand = Math.random) {
  const total = OUTREACH_ANGLES.length;
  let used = getUsedAngles(userId, charId);
  if (used.length >= total) {
    used = [];
    try { localStorage.removeItem(angleMemoryKey(userId, charId)); } catch { /* non-fatal */ }
  }
  const available = [];
  for (let i = 0; i < total; i++) if (!used.includes(i)) available.push(i);
  const idx = available[Math.floor(rand() * available.length)];
  rememberAngle(userId, charId, idx);
  return OUTREACH_ANGLES[idx];
}

export function buildOutreachPrompt(char, playerName, tone = DEFAULT_OUTREACH_TONE, angleOverride = null, opts = {}) {
  const toneGuide = OUTREACH_TONES[tone]?.prompt || OUTREACH_TONES.friendly.prompt;
  // An angle this character has not used on this player before, so the model
  // can't fall back into its 3-4 default openers AND can't re-draw one it just
  // used. A ritual day (birthday, anniversary, in-world holiday) passes its own
  // angle instead and does not consume one from the pool.
  const angle = angleOverride || pickOutreachAngle(opts.userId, char?.id);

  // Voice samples matter for the character's cadence — but ROTATE which quotes
  // we show (and cap at 3) so the opener isn't anchored to the same examples
  // every time.
  const quotes = Array.isArray(char.example_quotes) ? [...char.example_quotes] : [];
  for (let i = quotes.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [quotes[i], quotes[j]] = [quotes[j], quotes[i]]; }
  const voiceSamples = quotes.length > 0
    ? `\nVOICE SAMPLES — match this cadence/slang/punctuation, but do NOT reuse these lines verbatim:\n${quotes.slice(0, 3).map(q => `"${q}"`).join('\n')}`
    : '';

  // v421 (Haley, 2026-10-19): the character has READ everything before
  // texting — their whole bible, what the stories established, the writer's
  // own stories with them, the archive — and, when a thread already exists,
  // that thread's summary, facts, dynamics and last lines. The message must
  // hinge on one concrete thing from it; a thread is continued, never reopened.
  const research = String(opts.research || '').trim();
  const continuing = !!opts.continuing;
  const situation = continuing
    ? `You are texting ${playerName} AGAIN, in the conversation the two of you already have. This is NOT a first message and you do not introduce yourself. Pick up from where the thread actually is: something they told you, something you promised, something that happened between you, an open thread in your own life that they already know about.`
    : `You are sending a FIRST MESSAGE to ${playerName} — the opening line of a brand-new conversation. You're initiating contact.`;
  const relevance = research
    ? `RELEVANCE, NON-NEGOTIABLE: your message hinges on ONE concrete thing from EVERYTHING YOU HAVE READ above — a fact you know about them, a moment from a story you lived together, an open thread of your own life, your history. Name the specific thing. If nothing above concerns them yet, draw on your own canon life and world, never on an invented shared memory.`
    : '';

  return `You are ${char.name}, a ${char.species}.

PERSONALITY: ${char.personality || ''}
SPEECH PATTERN: ${char.speechPattern || ''}
BACKSTORY: ${char.backstory || ''}${voiceSamples}
${research ? `
═══ EVERYTHING YOU HAVE READ BEFORE WRITING (your whole life, and what is real between you and ${playerName}) ═══
${research}
` : ''}
${situation}

${continuing ? `WHAT TO PICK UP (build the message FROM the thread above; the angle below is only a flavor of HOW, never a reason to ignore what is already between you):` : `OPENING ANGLE FOR THIS MESSAGE (build the opener FROM this premise, in your own voice — do not state the premise flatly, just live it):`}
${angle}

TONE: ${toneGuide}
${relevance ? `
${relevance}
` : ''}
FOCUS: Do NOT mention any canon romantic partners, mates, or love interests from your backstory. Your attention is on ${playerName} right now.

${relationshipFrameFor(tone)}

RULES:
- 1–3 short sentences. Natural and in-character — not a monologue.
- Be SPECIFIC and CONCRETE — a real detail, moment, or thought, not generic small talk ("hey, how are you", "just wanted to reach out" are BANNED).
- Commit fully to the opening angle above so this message is unlike your other openers.
- *action text* sparingly (one short action at most).
- Match your speech patterns exactly — rhythm, slang, punctuation, emoji use or non-use.
- No quotation marks around your dialogue — write it naturally.
- Do NOT open with "Hey" or the character's name. Vary the very first word.

Write ONLY the message. No meta-text, no labels, no explanations.`;
}

// ─── USER OUTREACH PREFERENCES ─────────────────────────────────────────────
const OUTREACH_PREFS_KEY = 'sixguys_outreach_prefs';

export function getOutreachPrefs(userId) {
  try {
    const stored = localStorage.getItem(`${OUTREACH_PREFS_KEY}_${userId}`);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {
    enabled: true,
    defaultTone: DEFAULT_OUTREACH_TONE,
    blockedCharacters: [], // character IDs the user doesn't want to hear from
  };
}

export function saveOutreachPrefs(userId, prefs) {
  try {
    localStorage.setItem(`${OUTREACH_PREFS_KEY}_${userId}`, JSON.stringify(prefs));
  } catch {}
}

export default {
  getOutreachCandidate,
  canDoOutreach,
  buildOutreachPrompt,
  getOutreachPrefs,
  saveOutreachPrefs,
  recordOutreach,
  OUTREACH_TONES,
  DEFAULT_OUTREACH_TONE,
};
